import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useFeatures } from "@/lib/features";
import { useViaCep } from "@/lib/viacep";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

const COMPANY = [["razao_social", "Razão social"], ["fantasia", "Nome fantasia"], ["cnpj", "CNPJ"], ["ie", "Inscrição estadual (ou ISENTO)"],
  ["im", "Inscrição municipal"], ["uf", "UF"], ["cod_mun", "Código IBGE do município (7 dígitos)"], ["cep", "CEP"], ["endereco", "Endereço"],
  ["numero", "Número"], ["complemento", "Complemento"], ["bairro", "Bairro"], ["fone", "Telefone"], ["email", "E-mail"]];
const TAX = [["cfop_nfce", "CFOP das NFC-e"], ["cfop_nfe", "CFOP das NF-e"], ["cst_saida", "CST ICMS das saídas (origem + CST)"],
  ["aliq_saida", "Alíquota ICMS saídas (%)"], ["cst_entrada_simples", "CST p/ compras de fornecedor do Simples"],
  ["cod_receita", "Código de receita do ICMS (E116)"], ["dia_vencimento", "Dia de vencimento do ICMS"]];
const ACC = [["contador_nome", "Nome"], ["contador_cpf", "CPF"], ["contador_crc", "CRC"], ["contador_cnpj", "CNPJ do escritório"],
  ["contador_cep", "CEP"], ["contador_endereco", "Endereço"], ["contador_numero", "Número"], ["contador_bairro", "Bairro"],
  ["contador_fone", "Telefone"], ["contador_email", "E-mail"], ["contador_cod_mun", "Código IBGE do município"]];
const sel = "mt-1 h-10 w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 text-sm text-zinc-100";

function Fields({ list, s, set }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {list.map(([k, l]) => (
        <div key={k}>
          <Label className="text-xs font-mono-caps text-zinc-400">{l}</Label>
          <Input data-testid={`sped-${k}`} value={s[k] ?? ""} onChange={(e) => set(k, e.target.value)} className="mt-1 bg-zinc-900 border-zinc-800" />
        </div>
      ))}
    </div>
  );
}

export function SpedSettingsCard() {
  const { enabled } = useFeatures();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const on = enabled("fiscal.sped");
  useEffect(() => { if (on) { api.get("/fiscal/sped/settings").then(({ data }) => setS(data)).catch(() => setS(null)); } }, [on]);
  useViaCep(s?.cep, (d) => setS((p) => ({
    ...p,
    endereco: d.logradouro || p?.endereco,
    bairro: d.bairro || p?.bairro,
    uf: (d.uf || "").toUpperCase() || p?.uf,
    cod_mun: d.ibge || p?.cod_mun,
  })));
  useViaCep(s?.contador_cep, (d) => setS((p) => ({
    ...p,
    contador_endereco: d.logradouro || p?.contador_endereco,
    contador_bairro: d.bairro || p?.contador_bairro,
    contador_cod_mun: d.ibge || p?.contador_cod_mun,
  })));
  if (!on || !s) return null;
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    const { missing, ...body } = s;
    if (body.aliq_saida !== "" && body.aliq_saida != null) body.aliq_saida = Number(String(body.aliq_saida).replace(",", "."));
    if (body.dia_vencimento !== "" && body.dia_vencimento != null) body.dia_vencimento = Number(body.dia_vencimento);
    try { const { data } = await api.patch("/fiscal/sped/settings", body); setS(data); toast.success("Dados do SPED salvos"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="sped-settings-card" className="rounded-xl border border-zinc-800 bg-zinc-950/70 p-5 md:p-7 space-y-5">
      <div>
        <h2 className="font-display text-lg font-bold uppercase text-zinc-100">Fiscal — SPED (EFD ICMS/IPI)</h2>
        <p className="text-xs text-zinc-500 mt-1">Dados usados para gerar o arquivo do SPED Fiscal em Notas fiscais. Confira com o seu contador.</p>
        {s.missing?.length > 0 && <p data-testid="sped-missing" className="mt-2 text-xs text-amber-300">Falta preencher: {s.missing.join(", ")}.</p>}
      </div>
      <Fields list={COMPANY} s={s} set={set} />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div><Label className="text-xs font-mono-caps text-zinc-400">Perfil</Label>
          <select data-testid="sped-perfil" value={s.perfil} onChange={(e) => set("perfil", e.target.value)} className={sel}><option value="A">A</option><option value="B">B</option><option value="C">C</option></select></div>
        <div><Label className="text-xs font-mono-caps text-zinc-400">Atividade</Label>
          <select data-testid="sped-ind-ativ" value={s.ind_ativ} onChange={(e) => set("ind_ativ", e.target.value)} className={sel}><option value="1">Outros (comércio/serviço)</option><option value="0">Industrial</option></select></div>
        <div><Label className="text-xs font-mono-caps text-zinc-400">Regime</Label>
          <select data-testid="sped-regime" value={s.regime} onChange={(e) => set("regime", e.target.value)} className={sel}><option value="simples">Simples Nacional</option><option value="normal">Regime normal (com crédito de ICMS)</option></select></div>
      </div>
      <h3 className="font-display text-sm font-bold uppercase text-zinc-100">Tributação padrão</h3>
      <Fields list={TAX} s={s} set={set} />
      <h3 className="font-display text-sm font-bold uppercase text-zinc-100">Contador</h3>
      <Fields list={ACC} s={s} set={set} />
      <Button data-testid="sped-save" onClick={save} disabled={busy} className="h-11 bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-2" /> Salvar</>)}
      </Button>
    </div>
  );
}
